import {
  Text,
  Box,
  Modal as ModalMantine,
  Button,
  useMantineTheme,
  Flex,
} from "@mantine/core";
import { TickCircle, CloseCircle, InfoCircle } from "iconsax-react";
import TextHTML from "../../TextHTML";
import { useTranslation } from "react-i18next";

interface IProps {
  type: "success" | "danger" | "warning" | "info" | "successSpecial";
  description?: string;
  open: boolean;
  btnclose?: boolean;
  firstClick?: () => void;
  secondClick?: () => void;
  setClose: () => void;
  titleFirstButton?: string;
  titleSeccondButton?: string;
  contentEspecial?: React.ReactNode;
  textModal?: string;
  isLoading?: boolean;
}

interface IModal {
  backgroundColor: string;
  icon: React.ReactNode;
  textModal: string;
  varinantFirstButton: string;
  fz?: string;
}
interface IVariantModals {
  danger: IModal;
  info: IModal;
  success: IModal;
  warning: IModal;
  successSpecial: IModal;
}

const Modal = ({
  description,
  open,
  type,
  setClose,
  btnclose,
  firstClick,
  secondClick,
  titleFirstButton,
  titleSeccondButton,
  contentEspecial,
  textModal,
  isLoading = false,
}: IProps) => {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const variantModals: IVariantModals = {
    danger: {
      backgroundColor: theme.colors.alertsOpacity[0],
      icon: (
        <CloseCircle size="60" color={theme.colors.alerts[0]} variant="Bold" />
      ),
      textModal: t("global.modal.danger.textModal"),
      varinantFirstButton: "danger",
    },
    info: {
      backgroundColor: theme.colors.primaryOpacity[1],
      icon: (
        <InfoCircle size="60" color={theme.colors.primary[1]} variant="Bold" />
      ),
      textModal: t("global.modal.info.textModal"),
      varinantFirstButton: "primary",
    },
    success: {
      backgroundColor: theme.colors.complementaryOpacity[0],
      icon: (
        <TickCircle size="60" color={theme.colors.alerts[2]} variant="Bold" />
      ),
      textModal: t("global.modal.success.textModal"),
      varinantFirstButton: "primary",
    },
    warning: {
      backgroundColor: theme.colors.alertsOpacity[1],
      icon: (
        <InfoCircle size="60" color={theme.colors.alerts[1]} variant="Bold" />
      ),
      textModal: t("global.modal.warning.textModal"),
      varinantFirstButton: "danger",
    },
    successSpecial: {
      backgroundColor: theme.colors.complementaryOpacity[0],
      icon: (
        <TickCircle size="60" color={theme.colors.alerts[2]} variant="Bold" />
      ),
      textModal: t("global.modal.successSpecial.textModal"),
      fz: "22px",
      varinantFirstButton: "primary",
    },
  };

  const variantModalSelected = variantModals[type];

  return (
    <ModalMantine
      size="auto"
      opened={open}
      onClose={setClose}
      closeOnClickOutside={false}
      transitionProps={{
        exitDuration: 600,
        transition: "slide-up",
        duration: 600,
        timingFunction: "ease",
      }}
      centered
      styles={{
        content: {
          background: variantModalSelected.backgroundColor,
          borderRadius: "12px",
        },
        inner: { zIndex: 1004 },
        overlay: { zIndex: 1003 },
        header: {
          marginBottom: "0",
          background: "transparent",
          height: "75px",
        },
        close: {
          display: !!btnclose ? "block" : "none",
          position: "absolute",
          top: "12px",
          right: "12px",
          svg: {
            height: "24px",
            width: "24px",
          },
        },
        body: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#fff",
          borderRadius: "50px 50px 12px 12px",
          padding: "0 40px 40px 40px",
        },
      }}
    >
      <Box mt="-32px">{variantModalSelected.icon}</Box>
      <Text
        fw={600}
        fz={variantModalSelected.fz ? variantModalSelected.fz : "32px"}
        lh="32px"
        maw={420}
        ta="center"
        style={{ wordBreak: "break-word", whiteSpace: "wrap" }}
      >
        {textModal ? textModal : variantModalSelected.textModal}
      </Text>
      {description ? (
        <TextHTML
          maw={420}
          ta="center"
          fw={600}
          fz="16px"
          lh="24px"
          color="basic.4"
          mt="32px"
          mb="32px"
          textHTML={description}
        />
      ) : null}
      {contentEspecial}
      <Flex gap="16px" direction={"column"} w="100%" h="100%">
        {!!titleFirstButton && (
          <Button
            variant={variantModalSelected.varinantFirstButton}
            fullWidth
            onClick={firstClick}
            loading={isLoading}
          >
            {titleFirstButton}
          </Button>
        )}
        {!!titleSeccondButton && (
          <Button
            variant="primaryOutline"
            fullWidth
            onClick={secondClick}
            loading={isLoading}
          >
            {titleSeccondButton}
          </Button>
        )}
      </Flex>
    </ModalMantine>
  );
};

export default Modal;
